import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { SegmentedControl } from '../components/SegmentedControl'
import { FilterChipsRow } from '../components/FilterChipsRow'
import { MonthNavHeader } from '../components/MonthNavHeader'
import { EventRow } from '../components/EventRow'
import { CalendarGrid } from '../components/CalendarGrid'
import { api, type ApiEvent } from '../services/api'
import { getMonthViewInfo, isEventOnDay, getCalendarDays } from '../utils/monthView'
import { getEventListParams } from '../utils/eventFilterParams'
import styles from './HomeScreen.module.css'

export function HomeScreen() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [mode, setMode] = useState<'board' | 'calendar'>('board')
  const [events, setEvents] = useState<ApiEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [activeFilter, setActiveFilter] = useState('All')

  useEffect(() => {
    api.events.list(getEventListParams(activeFilter))
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [activeFilter]);

  const [viewDate, setViewDate] = useState(new Date());
  const { year, month, monthLabel, firstDayOfMonth, daysInMonth } = getMonthViewInfo(viewDate);

  return (
    <div className={styles.container}>
      {mode === 'board' ? (
        <>
          <div className={styles.header}>
            <h2>What's on</h2>
            <SegmentedControl name="hm" value={mode} options={[
              { value: 'board', label: 'List' },
              { value: 'calendar', label: 'Cal' },
            ]} onChange={v => setMode(v as 'board' | 'calendar')} />
          </div>
          <p className={styles.subtitle}>
            Everything on the island, in order. No feed, no boost.
          </p>

          <FilterChipsRow activeFilter={activeFilter} onSelect={setActiveFilter} className={styles.filterChips} />

          {loading ? (
            <div className={styles.loading}>Loading...</div>
          ) : events.length === 0 ? (
            <div className={styles.noEvents}>No events found.</div>
          ) : (
            <div className={styles.eventList}>
              {events.map(ev => <EventRow key={ev.id} event={ev} onClick={() => navigate(`/event/${ev.id}`)} />)}
            </div>
          )}

          {user?.role === 'admin' && (
            <div className={styles.eventRowContainer}>
              <button
                className={styles.postEventButton}
                onClick={() => navigate('/create')}
              >
                ＋ Post an event
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <FilterChipsRow activeFilter={activeFilter} onSelect={setActiveFilter} className={styles.filterChips} />
          <MonthNavHeader
            monthLabel={monthLabel}
            onPrev={() => setViewDate(new Date(year, month - 1, 1))}
            onNext={() => setViewDate(new Date(year, month + 1, 1))}
            className={styles.calendarHeader}
            navBtnClassName={styles.navBtn}
          />
          <div className={styles.calendarContainer}>
            <CalendarGrid
              days={getCalendarDays(firstDayOfMonth, daysInMonth)}
              hasEvent={(day) => day > 0 && events.some(e => isEventOnDay(e.event_date, day, month, year))}
              onClick={(day) => {
                const ev = events.find(e => isEventOnDay(e.event_date, day, month, year));
                if (ev) navigate(`/event/${ev.id}`);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
